// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import SharedModal from "src/components/shared/modals/SharedModal/SharedModal";
import LinkTableCardModal from "../LinkTableCardModal/LinkTableCardModal";
import { LinkIcon, VRIcon } from "src/assets/icons";

// Styles
import { useTheme } from "@mui/material/styles";

// Types
import { DeviceType } from "src/services/devices/types";
import { ExamType } from "src/services/exams/types";

// Hooks
import { useLinkExamDevice } from "src/services/devices/hooks/useLinkExamDevice";
import { useUnlinkExamFromDevice } from "src/services/devices/hooks/useUnlinkExamDevice";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useDevicesContext } from "src/contexts/devices/DevicesContext";

type LinkDeviceTableCardType = {
  device: DeviceType | undefined;
  exam: ExamType;
  examStatus: { status?: string; timelineStage?: number } | undefined;
};

const LinkDeviceTableCard = ({
  device,
  exam,
  examStatus,
}: LinkDeviceTableCardType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const { unlinkedDeviceOptions } = useDevicesContext();

  const theme = useTheme();
  const linkedBackgroundColor =
    theme.palette.mode === "dark" ? "#3B868E" : "#202338";

  const { mutate: linkDeviceMutate } = useLinkExamDevice(clinicInfo?.id);
  const { mutate: unlinkDeviceMutate } = useUnlinkExamFromDevice(
    clinicInfo?.id
  );

  const [openLinkModal, setOpenLinkModal] = useState(false);
  const [openUnlinkModal, setOpenUnlinkModal] = useState(false);

  const hasDevice = useMemo(
    () => device !== null && device !== undefined,
    [device]
  );

  const tooltipText = useMemo(() => {
    if (examStatus?.status === t("Completed")) {
      return t("This exam has already been completed");
    } else if (examStatus?.status === t("Terminated")) {
      return t("This exam has already been terminated");
    } else if (examStatus?.status === t("Invalid")) {
      return t("This exam has already been completed");
    } else if (hasDevice) {
      return t(
        "This exam has already been linked to a device: {{deviceAlias}}",
        {
          deviceAlias: device?.alias,
        }
      );
    } else {
      return t("Link Device");
    }
  }, [t, hasDevice, examStatus?.status, device?.alias]);

  const isDisabled = useMemo(
    () =>
      examStatus?.status === t("Completed") ||
      examStatus?.status === t("Terminated") ||
      examStatus?.status === t("Invalid"),
    [t, examStatus]
  );

  const unlinkDevice = useCallback(() => {
    unlinkDeviceMutate(device?.id ?? "", {
      onSuccess: () => setOpenUnlinkModal(false),
    });
  }, [device?.id, unlinkDeviceMutate]);

  const linkDevice = useCallback(
    (deviceId: string) => {
      linkDeviceMutate(
        { deviceId, exam },
        { onSuccess: () => setOpenLinkModal(false) }
      );
    },
    [exam, linkDeviceMutate]
  );

  return (
    <>
      <Box
        data-testid={`link-device-table-card-${exam.id}`}
        sx={{
          height: "100%",
          borderRadius: "8px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
          color: "#FFF",
          display: "flex",
          marginRight: "12px",

          cursor: isDisabled ? "default" : "pointer",
          backgroundColor: hasDevice
            ? linkedBackgroundColor
            : isDisabled
              ? "#5B6D7A70"
              : "#E2772E",
          "&:hover": {
            backgroundColor: hasDevice
              ? linkedBackgroundColor
              : isDisabled
                ? "#5B6D7A70"
                : "#CE631A",
          },
        }}
        onClick={() => {
          // if there is only 1 device available, auto-link it to the exam
          if (unlinkedDeviceOptions.length === 1 && !hasDevice && !isDisabled) {
            const deviceToLink = unlinkedDeviceOptions[0];
            linkDevice(deviceToLink.value);
          }
          // else if there are multiple devices available, open the link modal
          else if (!hasDevice && !isDisabled) {
            setOpenLinkModal(true);
          }
          // else if a device is already linked, open the unlink modal
          else if (hasDevice && !isDisabled) {
            setOpenUnlinkModal(true);
          }
        }}
      >
        <Tooltip title={tooltipText}>
          {hasDevice ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignContent: "center",
                alignItems: "center",
              }}
            >
              <VRIcon />
              <Typography
                sx={{
                  display: "block",
                  width: "100%",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  textAlign: "center",
                  px: "8px",
                  fontSize: "14px",
                }}
              >
                {device?.alias}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                alignItems: "center",
                alignContent: "center",
              }}
            >
              <VRIcon />
              <LinkIcon />
            </Box>
          )}
        </Tooltip>
      </Box>

      <LinkTableCardModal
        open={openLinkModal}
        onClose={() => setOpenLinkModal(false)}
        onConfirm={linkDevice}
      />

      {/* Unlink Device Modal */}
      <SharedModal
        title={t("Unlink Device?")}
        modalDescription={t(
          "Are you sure you want to unlink the exam from this device?"
        )}
        confirmButtonText={t("Unlink")}
        open={openUnlinkModal}
        onClose={() => setOpenUnlinkModal(false)}
        onConfirm={unlinkDevice}
      />
    </>
  );
};

export default LinkDeviceTableCard;
